/**
 * DatabaseRenderer — structured table rendering for PostgreSQL query results.
 * Supports sorting by clicking column headers.
 */

import { useState, useMemo } from 'react'
import { Database, ArrowUpDown, ArrowUp, ArrowDown, Copy, Check } from 'lucide-react'

interface DbResult {
  columns?: string[]
  rows?: unknown[]
  row_count?: number
  [k: string]: unknown
}

type SortDir = 'asc' | 'desc' | null

export function DatabaseResultRenderer({ data }: { data: Record<string, unknown> }) {
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>(null)
  const [copied, setCopied] = useState(false)

  const dbResult = data as DbResult

  // Extract columns and rows
  const { columns, rows } = useMemo(() => {
    let cols: string[] = []
    let rowData: Record<string, unknown>[] = []

    if (dbResult.columns && Array.isArray(dbResult.columns)) {
      cols = dbResult.columns as string[]
    }
    if (dbResult.rows && Array.isArray(dbResult.rows)) {
      rowData = dbResult.rows as Record<string, unknown>[]
      // If columns not provided, infer from first row
      if (cols.length === 0 && rowData.length > 0) {
        if (typeof rowData[0] === 'object' && rowData[0] !== null && !Array.isArray(rowData[0])) {
          cols = Object.keys(rowData[0])
        }
      }
    }

    return { columns: cols, rows: rowData }
  }, [dbResult])

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!sortCol || !sortDir) return rows
    return [...rows].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortCol]
      const bVal = (b as Record<string, unknown>)[sortCol]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDir === 'asc' ? -1 : 1
      if (bVal == null) return sortDir === 'asc' ? 1 : -1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal
      }
      const cmp = String(aVal).localeCompare(String(bVal))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [rows, sortCol, sortDir])

  const handleSort = (col: string) => {
    if (sortCol === col) {
      if (sortDir === 'asc') setSortDir('desc')
      else if (sortDir === 'desc') { setSortCol(null); setSortDir(null) }
      else setSortDir('asc')
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
  }

  const handleCopy = () => {
    const header = columns.join('\t')
    const body = sortedRows.map(r => {
      if (typeof r === 'object' && r !== null && !Array.isArray(r)) {
        return columns.map(c => String((r as Record<string, unknown>)[c] ?? '')).join('\t')
      }
      if (Array.isArray(r)) return (r as unknown[]).join('\t')
      return String(r)
    }).join('\n')
    navigator.clipboard.writeText(`${header}\n${body}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (columns.length === 0 && rows.length === 0) {
    return (
      <div className="text-center py-8">
        <Database className="w-8 h-8 text-subtle-foreground mx-auto mb-2" />
        <p className="text-xs text-muted-foreground">No data returned</p>
      </div>
    )
  }

  // Handle schema/table list results (non-tabular)
  if (columns.length === 0 && rows.length > 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Database className="w-4 h-4" />
            <span>{rows.length} result{rows.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <pre className="rounded-lg bg-foreground/[0.04] border border-subtle p-3 text-xs font-mono text-foreground overflow-auto max-h-96">
          {JSON.stringify(rows, null, 2)}
        </pre>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Database className="w-4 h-4" />
          <span>{rows.length} row{rows.length !== 1 ? 's' : ''} &middot; {columns.length} column{columns.length !== 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition px-2 py-1 rounded hover:bg-foreground/[0.06]"
        >
          {copied ? <Check className="w-3 h-3 text-success-foreground" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-subtle overflow-hidden">
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-foreground/[0.04] sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold w-8">#</th>
                {columns.map((col) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className="px-3 py-2 text-left text-[10px] uppercase tracking-wide text-muted-foreground font-semibold cursor-pointer hover:bg-foreground/[0.06] transition whitespace-nowrap select-none"
                  >
                    <span className="flex items-center gap-1">
                      {col}
                      {sortCol === col ? (
                        sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-accent" /> : <ArrowDown className="w-3 h-3 text-accent" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-subtle-foreground" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-subtle">
              {sortedRows.map((row, i) => (
                <tr key={i} className="hover:bg-accent-soft/30 transition">
                  <td className="px-2 py-1.5 text-subtle-foreground font-mono">{i + 1}</td>
                  {columns.map((col) => {
                    const val = typeof row === 'object' && row !== null && !Array.isArray(row)
                      ? (row as Record<string, unknown>)[col]
                      : Array.isArray(row) ? (row as unknown[])[columns.indexOf(col)] : row
                    const display = val === null || val === undefined
                      ? 'NULL'
                      : typeof val === 'object'
                        ? JSON.stringify(val)
                        : String(val)
                    const isNull = val === null || val === undefined
                    return (
                      <td
                        key={col}
                        className={`px-3 py-1.5 font-mono whitespace-nowrap max-w-[300px] truncate ${isNull ? 'text-subtle-foreground italic' : 'text-foreground'}`}
                        title={display}
                      >
                        {display}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
